import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../assets/logo-T-color.jpg";
import { useSetRecoilState } from "recoil";
import { authAtom } from "../../recoil/atoms/authAtom";
import routes from "../../router/routes";
import { APP_VERSION } from "../../constants/version";
import { useApiLoginUser } from "../../hooks/api/useApiLoginUser";
import { useApiGetUsersSearchOptions } from "../../hooks/api/useApiGetUserSearchOptions";
import { loginUserAtom } from "../../recoil/atoms/loginUserAtoms";
import {
  RequestChatworkSendMessageRequest,
  RequestGetAmazonItemsListRequest,
  RequestGetUserSearchOptionsRequest,
} from "../../services/OpenAPI";
import { userSearchOptionsAtom } from "../../recoil/atoms/userSearchOptionsAtoms";
import { useApiGetBaseSearchOptions } from "../../hooks/api/useApiGetBaseSearchOptions";
import { baseSearchOptionsAtom } from "../../recoil/atoms/baseSearchOptionsAtoms";
import { useApiGetStatusList } from "../../hooks/api/useApiGetStatusList";
import { statusListAtom } from "../../recoil/atoms/statusListAtoms";
import { useApiGetAmazonItems } from "../../hooks/api/useApiGetAmazonItems";
import { useApiChatworkSend } from "../../hooks/api/useApiChatworkSend";
import { useApiUpdateAmazonItems } from "../../hooks/api/useApiUpdateAmazonItems";
import ForgotPasswordDialog from "../../components/ForgotPasswordDialog";
import { Status } from "../../constants/status";
import { useApiGetTransportationOptions } from "../../hooks/api/useApiGetTransportationOptions";
import { transportationOptionsState } from "../../recoil/atoms/transportationOptionsAtoms";
import { useApiGetWarehouses } from "../../hooks/api/useApiGetWarehouses";
import { warehousesState } from "../../recoil/atoms/warehousesAtoms";
import {
  isBaseLevel,
  isGeneralUser,
  isNotOwner,
  isOwner,
} from "../../constants/roles";

/** n 日以上前なら true */
export const isOlderThan = (dateStr: string | undefined, days = 30) => {
  if (!dateStr) return false;
  const diff = Date.now() - new Date(dateStr).getTime();
  return diff > days * 24 * 60 * 60 * 1000;
};

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [mailAddress, setMailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const setAuthState = useSetRecoilState(authAtom);
  const setLoginUserState = useSetRecoilState(loginUserAtom);
  const setUserSearchOptionsState = useSetRecoilState(userSearchOptionsAtom);
  const setBaseSearchOptionsState = useSetRecoilState(baseSearchOptionsAtom);
  const setStatusListState = useSetRecoilState(statusListAtom);
  const setTransportationOptionsState = useSetRecoilState(
    transportationOptionsState
  );
  const setWarehousesState = useSetRecoilState(warehousesState);

  const {
    fetchLoginUser,
    isLoading,
    error: loginError,
    setError: setLoginError,
  } = useApiLoginUser();
  const { fetchUserSearchOptions, isLoading: isUserSearchLoading } =
    useApiGetUsersSearchOptions();
  const { fetchBaseSearchOptions, isLoading: isBaseSearchLoading } =
    useApiGetBaseSearchOptions();
  const { fetchStatusList } = useApiGetStatusList();
  const { fetchAmazonItems } = useApiGetAmazonItems();
  const { sendChatwoekMessage } = useApiChatworkSend();
  const { updateAmazonItems } = useApiUpdateAmazonItems();
  const { fetchTransportationOptions } = useApiGetTransportationOptions();
  const { fetchWarehouses } = useApiGetWarehouses();

  const [isForgotDialogOpen, setIsForgotDialogOpen] = useState(false);

  const handleLogin = async () => {
    if (!mailAddress || !password) {
      setErrorMessage("メールアドレスとパスワードを入力してください。");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(mailAddress)) {
      setErrorMessage("正しいメールアドレスを入力してください。");
      return;
    }

    try {
      const tenantId = 1; // Saas展開するまではとりあえずtenantIdは「1」にしておく
      const user = await fetchLoginUser(tenantId, mailAddress, password);
      if (user) {
        // ローカルストレージに保存
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("isAuthenticated", "true");

        // Recoilの状態を更新
        setAuthState({
          isAuthenticated: true,
          user: { mailAddress: mailAddress },
        });
        setLoginUserState({ user });

        // if (!user.baseId || !user.permissionId || !user.tenantId) return;
        if (
          user.baseId === null ||
          user.baseId === undefined ||
          user.permissionId == null ||
          user.tenantId == null
        )
          return;

        const request: RequestGetUserSearchOptionsRequest = {
          baseId: user.baseId,
          permissionId: user.permissionId,
          tenantId: user.tenantId,
        };

        // ユーザー検索バーの選択肢
        const userSearchOptions = await fetchUserSearchOptions(request);
        let filteredOptions: typeof userSearchOptions = [];

        if (isOwner(user.permissionId)) {
          // 管理者 → そのまま全件
          filteredOptions = userSearchOptions?.filter((u) => u.id !== 0) ?? [];
        } else if (isBaseLevel(user.permissionId)) {
          // 拠点責任者、拠点メンバー → 同じ拠点のユーザーのみ
          filteredOptions =
            userSearchOptions?.filter(
              (u) =>
                u.id !== 0 && u.baseId === user.baseId && isNotOwner(u.permissionId)
            ) ?? [];
        } else if (isGeneralUser(user.permissionId)) {
          // 一般ユーザー → 本人のみ
          filteredOptions =
            userSearchOptions?.filter((u) => u.id === user.id) ?? [];
        }

        setUserSearchOptionsState({
          userSearchOptions: filteredOptions,
        });

        // 拠点選択バーの選択肢
        const baseSearchOptions = await fetchBaseSearchOptions();
        let filteredBaseOptions = baseSearchOptions ?? [];

        if (isNotOwner(user.permissionId)) {
          const allowedBaseIds = (user.baseInfo ?? []).map((b) => b.id);
          filteredBaseOptions =
            baseSearchOptions?.filter((base) =>
              allowedBaseIds.includes(base.id!)
            ) ?? [];
        }

        setBaseSearchOptionsState({ baseSearchOptions: filteredBaseOptions });
        // ステータス一覧取得
        const productStatus = await fetchStatusList();
        if (!productStatus) throw "ステータスの取得に失敗";
        const filteredStatus = productStatus.filter(
          (status) => (status.id ?? 0) <= 8
        );
        setStatusListState({ statusList: filteredStatus });

        // 配送方法一覧取得
        const transportatinOptions = await fetchTransportationOptions();
        if (!transportatinOptions) throw "配送先の取得に失敗";
        setTransportationOptionsState(transportatinOptions);

        // 倉庫一覧取得
        const warehouses = await fetchWarehouses();
        if (!warehouses) throw "倉庫一覧の取得に失敗";
        setWarehousesState(warehouses);

        // トップへリダイレクト
        navigate(routes.toppage);
      } else {
        // setErrorMessage("ログインに失敗しました。もう一度お試しください。");
      }
    } catch (error) {
      setErrorMessage("予期しないエラーが発生しました。");
      console.error(error);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSignUp = () => navigate(routes.signUp);

  useEffect(() => {
    if (loginError === "401") {
      setErrorMessage("ログインに失敗しました。もう一度お試しください。");
    } else if (loginError === "403") {
      setErrorMessage(
        "未承認のユーザーです。この状態が続く場合は管理者へお問い合わせください。"
      );
    }
    setLoginError(null);
  }, [loginError]);

  return (
    <>
      <Box
        sx={{
          maxWidth: "400px",
          margin: "50px auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <ForgotPasswordDialog
          open={isForgotDialogOpen}
          onClose={() => setIsForgotDialogOpen(false)}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <img
            src={logo}
            alt="ラクロジロゴ"
            style={{ width: "150px", height: "auto" }}
          />
        </Box>

        {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

        <Typography
          variant="h6"
          sx={{
            alignSelf: "flex-start",
            marginTop: "15px",
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          ログインID
        </Typography>
        <TextField
          placeholder="Email Address"
          type="email"
          variant="outlined"
          fullWidth
          sx={{ marginTop: "5px", marginBottom: "15px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email />
              </InputAdornment>
            ),
          }}
          onChange={(e) => setMailAddress(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleLogin();
          }}
        />

        <Typography
          variant="h6"
          sx={{
            alignSelf: "flex-start",
            marginTop: "15px",
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          パスワード
        </Typography>
        <TextField
          placeholder="Password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          sx={{ marginTop: "5px", marginBottom: "15px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={togglePasswordVisibility} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleLogin();
          }}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={handleLogin}
          sx={{
            backgroundColor: "#4a90e2",
            color: "white",
            marginTop: "20px",
            "&:hover": {
              backgroundColor: "#357ac9",
            },
          }}
          disabled={isLoading}
        >
          {isLoading ? "ログイン中..." : "ログイン"}
        </Button>

        <Typography
          sx={{
            marginTop: "10px",
            fontSize: "12px",
            color: "#333",
            cursor: "pointer",
          }}
          onClick={() => setIsForgotDialogOpen(true)}
        >
          ※忘れた方はこちら
        </Typography>

        <Button
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: "#e74c3c",
            color: "white",
            marginTop: "20px",
            "&:hover": {
              backgroundColor: "#c0392b",
            },
          }}
          onClick={handleSignUp}
        >
          新規利用者登録はこちら
        </Button>
      </Box>
      <Box
        sx={{
          position: "fixed",
          bottom: "10px",
          left: "10px",
          display: "flex",
          alignItems: "center",
          gap: 2,
          fontSize: "12px",
          color: "#555",
          zIndex: 1000,
        }}
      >
        <Typography sx={{ color: "#aaa", fontSize: "11px" }}>
          v{APP_VERSION}
        </Typography>
        <Typography sx={{ color: "#999" }}>|</Typography>
        <Typography
          component={Link}
          to="/privacy"
          sx={{
            textDecoration: "none",
            color: "#555",
            "&:hover": { textDecoration: "underline", color: "#000" },
          }}
        >
          プライバシーポリシー
        </Typography>
        <Typography
          sx={{
            color: "#999",
          }}
        >
          |
        </Typography>
        <Typography
          component={Link}
          to="/terms"
          sx={{
            textDecoration: "none",
            color: "#555",
            "&:hover": { textDecoration: "underline", color: "#000" },
          }}
        >
          利用規約（EULA）
        </Typography>
      </Box>
    </>
  );
};

export default Login;
